// ─────────────────────────────────────────────────────────────────────
// <Footer /> — design vision spec
//
// 4-column desktop, stacked mobile.
// First col: brand mark + italic tagline + city.
// Other cols: micro headers + link lists.
// ─────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SntLogo from '../common/SntLogo';
import BrandLogo from '../tenant/BrandLogo';           // r7.6
import PoweredBy from '../tenant/PoweredBy';           // r7.6 fixed line on tenant screens
import { useLocation as useCity } from '../../context/LocationContext';
import { useTenant } from '../../context/TenantContext';   // r7.6

export default function Footer() {
  const { t } = useTranslation();
  const { city } = useCity();
  const tenant = useTenant();   // r7.6
  const year = new Date().getFullYear();

  // r7.6 — a tenant site gets a short footer: its mark, its own legal links (falling back to the platform's),
  // support, and the fixed "Powered by ShowzNTicketz" line. No marketplace columns, no SNT socials.
  if (!tenant.isPlatform) {
    const links = tenant.links || {};
    return (
      <footer className="border-t border-line" style={{ background: 'var(--snt-bg)' }} data-testid="tenant-footer">
        <div className="max-w-container mx-auto px-6 lg:px-9 pt-10 pb-8 grid gap-8 lg:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            <BrandLogo variant="full" size={32} themed />
            <div className="mt-4 text-[12px] text-text-3">
              {t('footer.in_city', 'Currently in')}{' '}
              <span className="text-text">{city || 'Bengaluru'}</span>
            </div>
          </div>
          <FootCol title={t('footer.account', 'Account')}>
            <FootLink to="/me/bookings">{t('nav.bookings', 'My bookings')}</FootLink>
            <FootLink to="/me/preferences">{t('nav.preferences', 'Preferences')}</FootLink>
            <FootLink to="/me/consents">{t('tenant.consents', 'Marketing consent')}</FootLink>
          </FootCol>
          <FootCol title={tenant.name}>
            <FootExt href={links.terms || '/terms'}>{t('footer.terms', 'Terms')}</FootExt>
            <FootExt href={links.privacy || '/privacy'}>{t('footer.privacy', 'Privacy')}</FootExt>
            <FootExt href={links.support || tenant.supportHref}>{t('footer.contact', 'Contact us')}</FootExt>
            <FootLink to="/refund-policy">{t('footer.refund', 'Refund policy')}</FootLink>
          </FootCol>
        </div>
        <div className="border-t border-line">
          <div className="max-w-container mx-auto px-6 lg:px-9 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-[11px] text-text-3">© {year} {tenant.name}</div>
            <PoweredBy />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="border-t border-line"
      style={{ background: 'var(--snt-bg)' }}
    >
      <div className="max-w-container mx-auto px-9 pt-12 pb-9 grid gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
        {/* Brand col */}
        <div>
          <SntLogo variant="full" size={36} themed />
          <div
            className="mt-3 text-[16px] italic"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--snt-text-2)' }}
          >
            {t('footer.tagline', 'Showz happen. Be there.')}
          </div>
          <div className="mt-5 text-[12px] text-text-3">
            {t('footer.in_city', 'Currently in')}{' '}
            <span className="text-text">{city || 'Bengaluru'}</span>
          </div>
        </div>

        <FootCol title={t('footer.discover', 'Discover')}>
          <FootLink to="/v/live-events">{t('nav.events', 'Events')}</FootLink>
          <FootLink to="/v/sports">{t('nav.sports', 'Sports')}</FootLink>
          <FootLink to="/v/theatre">{t('nav.plays', 'Plays')}</FootLink>
          <FootLink to="/v/attractions">{t('nav.activities', 'Activities')}</FootLink>
          <FootLink to="/m/family-day">{t('footer.family', 'For families')}</FootLink>
          <FootLink to="/curated/this-week">{t('footer.curated', 'Curated this week')}</FootLink>
        </FootCol>

        <FootCol title={t('footer.account', 'Account')}>
          <FootLink to="/me/bookings">{t('nav.bookings', 'My bookings')}</FootLink>
          <FootLink to="/me/wishlist">{t('nav.wishlist', 'Wishlist')}</FootLink>
          <FootLink to="/me/preferences">{t('nav.preferences', 'Preferences')}</FootLink>
          <FootLink to="/me/consents">{t('tenant.consents', 'Marketing consent')}</FootLink>
          <FootLink to="/help">{t('footer.help', 'Help & FAQ')}</FootLink>
          <FootLink to="/refund-policy">{t('footer.refund', 'Refund policy')}</FootLink>
          <FootLink to="/contact">{t('footer.contact', 'Contact us')}</FootLink>
        </FootCol>

        <FootCol title={t('footer.company', 'Company')}>
          <FootLink to="/about">{t('footer.about', 'About')}</FootLink>
          <FootLink to="/organizer/onboarding">{t('footer.list_event', 'List your event')}</FootLink>
          <FootLink to="/terms">{t('footer.terms', 'Terms')}</FootLink>
          <FootLink to="/privacy">{t('footer.privacy', 'Privacy')}</FootLink>
          <FootLink to="/security">{t('footer.security', 'Security')}</FootLink>
          <FootLink to="/api/docs">{t('footer.api', 'Developer API')}</FootLink>
        </FootCol>
      </div>

      <div className="border-t border-line">
        <div className="max-w-container mx-auto px-9 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-[11px] text-text-3">
            © {year} Showz N Ticketz · Avisatech Services Pvt Ltd · Bengaluru
          </div>
          <div className="text-[11px] text-text-3 flex gap-4">
            <a href="https://twitter.com/showznticketz" className="hover:text-accent no-underline">Twitter</a>
            <a href="https://instagram.com/showznticketz" className="hover:text-accent no-underline">Instagram</a>
            <a href="https://www.linkedin.com/company/showznticketz" className="hover:text-accent no-underline">LinkedIn</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FootCol({ title, children }) {
  return (
    <div>
      <h4
        className="snt-micro mb-3.5"
        style={{ color: 'var(--snt-text-3)' }}
      >
        {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/** r7.6 — tenant legal links may be absolute URLs (their own site) or mailto:. */
function FootExt({ href, children }) {
  const external = /^(https?:|mailto:)/.test(href || '');
  if (!external) return <FootLink to={href}>{children}</FootLink>;
  return (
    <a
      href={href}
      target={href.startsWith('mailto:') ? undefined : '_blank'}
      rel="noopener noreferrer"
      className="block text-[13px] py-1 transition-colors no-underline"
      style={{ color: 'var(--snt-text-2)' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--snt-accent)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--snt-text-2)'; }}
    >
      {children}
    </a>
  );
}

function FootLink({ to, children }) {
  return (
    <Link
      to={to}
      className="block text-[13px] py-1 transition-colors no-underline"
      style={{ color: 'var(--snt-text-2)' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--snt-accent)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--snt-text-2)'; }}
    >
      {children}
    </Link>
  );
}
